/**
 * TopMessages — shows top-performing outbound messages that got positive replies.
 */

import { useQuery } from '@tanstack/react-query'
import { MessageSquare } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { useApi } from '@/hooks/use-api'

type TopMessage = {
  readonly content: string
  readonly strategy: string
  readonly lead_name: string
  readonly reply_sentiment: string
}

type TopMessagesResponse = {
  readonly messages: readonly TopMessage[]
}

const STRATEGY_DISPLAY: Record<string, string> = {
  pain_agitate: 'Pain Agitate',
  mutual_value: 'Mutual Value',
  social_proof: 'Social Proof',
  curiosity_gap: 'Curiosity Gap',
  direct_expert: 'Direct Expert',
  challenge: 'Challenge',
  trojan_horse: 'Trojan Horse',
  mutual_connection: 'Mutual Connection',
}

export function TopMessages() {
  const apiFetch = useApi()

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', 'top-messages'],
    queryFn: async (): Promise<TopMessagesResponse> => {
      const res = await apiFetch<TopMessagesResponse>('/api/leads/analytics/top-messages')
      if (!res.success || !res.data) return { messages: [] }
      return res.data
    },
    staleTime: 60_000,
  })

  const messages = data?.messages ?? []

  if (isLoading) {
    return (
      <Card>
        <CardHeader><CardTitle className="text-sm font-semibold">Top Messages</CardTitle></CardHeader>
        <CardContent><Skeleton className="h-32 w-full" /></CardContent>
      </Card>
    )
  }

  if (messages.length === 0) {
    return (
      <Card>
        <CardHeader><CardTitle className="text-sm font-semibold">Top Messages</CardTitle></CardHeader>
        <CardContent className="py-8 text-center">
          <MessageSquare className="mx-auto h-6 w-6 text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">No message performance data yet.</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold">Top Messages</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="divide-y">
          {messages.map((msg, i) => (
            <div key={i} className="px-4 py-3">
              <div className="flex items-center gap-2 mb-1">
                <Badge variant="outline" className="text-[10px]">
                  {STRATEGY_DISPLAY[msg.strategy] ?? msg.strategy}
                </Badge>
                <span className="text-xs text-muted-foreground">{msg.lead_name}</span>
                <Badge className="text-[10px] bg-emerald-500/12 text-emerald-300 border-0 ml-auto">
                  {msg.reply_sentiment}
                </Badge>
              </div>
              <p className="text-sm text-foreground/80 line-clamp-2">{msg.content}</p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
